import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faArrowLeft, faSearch, faPhone, faComment, faTrash, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

const ContactBook = ({ isOpen, onClose, username, password, cloudId, setShowNewMessageModal, onSelectRecipient }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(true);
  const [sending, setSending] = useState(false);  // New state for view mode
  const [searchQuery, setSearchQuery] = useState('');
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    workPhone: '',
    cellPhone: '',
    homePhone: '',
    email: '',
    fax: '',
    tags: ''
  });
  // Add state to preserve original contact details
  const [originalContact, setOriginalContact] = useState({});
  const [activeLetter, setActiveLetter] = useState(null);
  const [showLetterIndicator, setShowLetterIndicator] = useState(false);
  const sectionRefs = useRef({});
  const touchStartY = useRef(null);
  const touchCurrentLetter = useRef(null);
  const [showNumberSelect, setShowNumberSelect] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState('');
  const [actionType, setActionType] = useState(''); // 'call' or 'message'

  useEffect(() => {
    if (isOpen) {
      getContacts();
    }
  }, [isOpen]);

  const getContacts = () => {
    const payLoad = new FormData();
    payLoad.append('domain', username.split('@')[1]);
    payLoad.append('user', username.split('@')[0]);
    payLoad.append('cloud_id', cloudId);
      
    axios.post(
      `${process.env.REACT_APP_API_URL}?format=json&object=contact&action=read&utype=u&cloud_username=${username}&cloud_password=${password}`,
      payLoad
    ).then(response => {
      setContacts(response.data);
    }).catch(error => {
      console.log(error);
    });
  };

  // Transform contacts array into sorted list
  const contactsList = useMemo(() => {
    return contacts
      .filter(contact => !contact.tags.includes('ext')) // Filter out contacts with 'ext' tag
      .map(contact => ({
        firstName: contact.first_name,
        lastName: contact.last_name,
        workPhone: contact.work_phone,
        fullName: `${contact.first_name} ${contact.last_name}`.trim(),
        contactId: contact.contact_id,
        email: contact.email,
        cellPhone: contact.cell_phone,
        homePhone: contact.home_phone,
        tags: contact.tags
      }))
      .sort((a, b) => a.firstName.localeCompare(b.firstName));
  }, [contacts]);

  // Group contacts by first letter of first name
  const groupedContacts = useMemo(() => {
    const filtered = contactsList.filter(contact =>
      contact.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (contact.workPhone && contact.workPhone.includes(searchQuery))
    );

    return filtered.reduce((groups, contact) => {
      const firstLetter = contact.firstName && contact.firstName[0] ? contact.firstName[0].toUpperCase() : contact.lastName && contact.lastName[0] ? contact.lastName[0].toUpperCase() : '';
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(contact);
      return groups;
    }, {});
  }, [contactsList, searchQuery]);

  // Get available letters for the index
  const availableLetters = useMemo(() => {
    return Object.keys(groupedContacts).sort();
  }, [groupedContacts]);

  // Handle letter selection
  const scrollToLetter = (letter) => {
    if (sectionRefs.current[letter]) {
      sectionRefs.current[letter].scrollIntoView({ behavior: 'smooth' });
      setActiveLetter(letter);
      setShowLetterIndicator(true);
      setTimeout(() => {
        setShowLetterIndicator(false);
        setActiveLetter(null);
      }, 1000);
    }
  };

  // Handle touch events for the index bar
  const handleTouchStart = (e, letter) => {
    touchStartY.current = e.touches[0].clientY;
    touchCurrentLetter.current = letter;
    scrollToLetter(letter);
  };

  const handleTouchMove = (e) => {
    if (!touchStartY.current) return;

    const touchY = e.touches[0].clientY;
    const indexBar = e.currentTarget;
    const rect = indexBar.getBoundingClientRect();
    const letterHeight = rect.height / availableLetters.length;
    const index = Math.floor((touchY - rect.top) / letterHeight);
    
    if (index >= 0 && index < availableLetters.length) {
      const letter = availableLetters[index];
      if (letter !== touchCurrentLetter.current) {
        touchCurrentLetter.current = letter;
        scrollToLetter(letter);
      }
    }
  };

  const handleTouchEnd = () => {
    touchStartY.current = null;
    touchCurrentLetter.current = null;
  };

  const handleAddContact = (e) => {
    e.preventDefault();

    if (sending) return; // Prevent multiple submissions
    setSending(true);

    // Validate required fields
    if (!newContact.firstName.trim() || !newContact.lastName.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    const payLoad = new FormData();
    payLoad.append('domain', username.split('@')[1]);
    payLoad.append('user', username.split('@')[0]);
    payLoad.append('cloud_id', cloudId);
    payLoad.append('first_name', newContact.firstName);
    payLoad.append('last_name', newContact.lastName);
    payLoad.append('home_phone', newContact.homePhone);
    payLoad.append('cell_phone', newContact.cellPhone);
    payLoad.append('work_phone', newContact.workPhone);
    payLoad.append('email', newContact.email);
    payLoad.append('fax', newContact.fax);

    const action = isEditMode ? 'update' : 'create';
    if (isEditMode) {
      payLoad.append('contact_id', newContact.contactId);
    }

    axios.post(
      `${process.env.REACT_APP_API_URL}?format=json&object=contact&action=${action}&utype=u&cloud_username=${username}&cloud_password=${password}`,
      payLoad
    ).then(response => {
      getContacts(); // Refresh contacts list
      setShowAddForm(false);
      setIsEditMode(false);
      setIsViewMode(true); // Ensure view mode after saving
      setNewContact({
        firstName: '',
        lastName: '',
        workPhone: '',
        cellPhone: '',
        homePhone: '',
        email: '',
        fax: '',
        tags: ''
      });
      toast.success('Contact saved successfully');
    }).catch(error => {
      console.log(error);
      toast.error('Failed to save contact. Please try again.');
    }).finally(() => {
      setSending(false); // Reset sending state whether request succeeds or fails
    });
  };

  const handleDeleteContact = () => {
    if (!newContact.contactId) return;

    confirmAlert({
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this contact?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            const payLoad = new FormData();
            payLoad.append('domain', username.split('@')[1]);
            payLoad.append('user', username.split('@')[0]);
            payLoad.append('contact_id', newContact.contactId);

            axios.post(
              `${process.env.REACT_APP_API_URL}?format=json&object=contact&action=delete&utype=u&cloud_username=${username}&cloud_password=${password}`,
              payLoad
            ).then(response => {
              getContacts(); // Refresh contacts list
              setShowAddForm(false);
              setIsEditMode(false);
              setIsViewMode(true); // Ensure view mode after deleting
              setNewContact({
                firstName: '',
                lastName: '',
                workPhone: '',
                cellPhone: '',
                homePhone: '',
                email: '',
                fax: '',
                tags: ''
              });
              toast.success('Contact deleted successfully');
            }).catch(error => {
              console.log(error);
              toast.error('Failed to delete contact. Please try again.');
            });
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ],
      overlayClassName: "contact-delete-overlay",
      className: "contact-delete-alert"
    });
  };

  const getAvailableNumbers = () => {
    const numbers = [];
    if (newContact.workPhone) numbers.push({ label: 'Work', number: newContact.workPhone });
    if (newContact.cellPhone) numbers.push({ label: 'Cell', number: newContact.cellPhone });
    if (newContact.homePhone) numbers.push({ label: 'Home', number: newContact.homePhone });
    return numbers;
  };

  const handleMessageClick = () => {
    const numbers = getAvailableNumbers();
    if (numbers.length === 0) {
      toast.error('No phone number available');
      return;
    }
    if (numbers.length === 1) {
      onSelectRecipient(numbers[0].number);
      setShowAddForm(false);
      onClose();
      setShowNewMessageModal(true);
    } else {
      setActionType('message');
      setSelectedNumber(numbers[0].number);
      setShowNumberSelect(true);
    }
  };

  const handleCallClick = () => {
    const numbers = getAvailableNumbers();
    if (numbers.length === 0) {
      toast.error('No phone number available');
      return;
    }
    if (numbers.length === 1) {
      makeCall(numbers[0].number);
    } else {
      setActionType('call');
      setSelectedNumber(numbers[0].number);
      setShowNumberSelect(true);
    }
  };

  const handleNumberSelectAction = () => {
    if (!selectedNumber) return;
    
    if (actionType === 'call') {
      makeCall(selectedNumber);
    } else {
      onSelectRecipient(selectedNumber);
      setShowAddForm(false);
      onClose();
      setShowNewMessageModal(true);
    }
    setShowNumberSelect(false);
  };

  const handleContactClick = (contact) => {
    const contactDetails = {
      firstName: contact.firstName,
      lastName: contact.lastName,
      workPhone: contact.workPhone || '',
      cellPhone: contact.cellPhone || '',
      homePhone: contact.homePhone || '',
      email: contact.email || '',
      fax: contact.fax || '',
      contactId: contact.contactId,
      tags: contact.tags
    };
    setNewContact(contactDetails);
    setOriginalContact(contactDetails); // Store original details
    setIsEditMode(false);
    setIsViewMode(true);
    setShowAddForm(true);
  };

  const handleEditClick = () => {
    if (isEditMode) {
      // Cancel was clicked - restore original values
      setNewContact(originalContact);
      setIsEditMode(false);
      setIsViewMode(true);
    } else {
      // Edit was clicked
      setIsEditMode(true);
      setIsViewMode(false);
    }
  };

  const handleBackClick = () => {
    setShowAddForm(false);
    setIsEditMode(false);
    setIsViewMode(true); // Ensure view mode when going back
    setNewContact({
      firstName: '',
      lastName: '',
      workPhone: '',
      cellPhone:'',
      homePhone:'',
      email:'',
      fax:'',
      tags: ''
    });
  };

  const makeCall = (number) => {
    window.location.href = `sip:${number}?dialAction=autoCall`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex sm:items-start sm:justify-center bg-black bg-opacity-50">
      <div className={`
        bg-white w-full flex flex-col
        sm:rounded-lg sm:shadow-xl sm:max-w-md sm:mx-4 sm:my-[60px] sm:h-[86vh]
        
        /* Mobile styles */
        fixed bottom-0 left-0 right-0
        h-[94vh] rounded-t-[20px] mt-[6vh]
        transform transition-transform duration-300 ease-out
        ${isOpen ? 'translate-y-0' : 'translate-y-full'}
        
        /* Desktop styles */
        sm:static sm:transform-none sm:rounded-lg sm:h-[86vh]
        relative
      `}>
        {/* Visual handle for mobile */}
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-3 mb-1 sm:hidden" />

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          {showAddForm ? (
            <>
              <button 
                onClick={handleBackClick}
                className="text-gray-600 hover:text-gray-800"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <h3 className="text-lg font-semibold text-gray-800">
                {isEditMode ? 'Edit Contact' : (isViewMode ? 'Contact Details' : 'Add New Contact')}
              </h3>
              {isViewMode && newContact.contactId && (
                <button 
                  onClick={handleEditClick}
                  style={(() => {
                    const styles = localStorage.getItem("messaging_styles");
                    let buttonStyles = {};
                    
                    if (styles) {
                      try {
                        const parsedStyles = JSON.parse(styles);
                        buttonStyles = {
                          color: parsedStyles.message_buttons_color || '#3B82F6'
                        };
                      } catch (e) {
                        console.error('Error parsing messaging styles:', e);
                      }
                    }
                    return buttonStyles;
                  })()}
                  className={`hover:opacity-80 transition-opacity duration-200 font-medium ${!localStorage.getItem("messaging_styles") ? 'text-blue-500 hover:text-blue-600' : ''}`}
                >
                  Edit
                </button>
              )}
              {isEditMode && (
                <button 
                  onClick={handleEditClick}
                  className="text-red-500 hover:text-red-600 font-medium"
                >
                  Cancel
                </button>
              )}
              {!isViewMode && !isEditMode && !newContact.contactId && <div className="w-8" />} {/* Spacer for alignment */}
            </>
          ) : (
            <>
             
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => {
                    setShowAddForm(true);
                    setIsEditMode(false);
                    setIsViewMode(false);
                    setNewContact({
                      firstName: '',
                      lastName: '',
                      workPhone: '',
                      cellPhone: '',
                      homePhone: '',
                      email: '',
                      fax: '',
                      tags: ''
                    });
                  }}
                  style={(() => {
                    const styles = localStorage.getItem("messaging_styles");
                    let buttonStyles = {};
                    
                    if (styles) {
                      try {
                        const parsedStyles = JSON.parse(styles);
                        buttonStyles = {
                          color: parsedStyles.message_buttons_color || '#3B82F6'
                        };
                      } catch (e) {
                        console.error('Error parsing messaging styles:', e);
                      }
                    }
                    return buttonStyles;
                  })()}
                  className={`hover:opacity-80 transition-opacity duration-200 ${!localStorage.getItem("messaging_styles") ? 'text-blue-500 hover:text-blue-600' : ''}`}
                >
                  <FontAwesomeIcon icon={faPlus} size="lg" />
                </button>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">My Contacts</h3>
              <button 
                  onClick={onClose}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <FontAwesomeIcon icon={faTimes} size="lg" />
                </button>
            </>
          )}
        </div>

        {/* Rest of the content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {showAddForm ? (
            <>
              {/* Action Buttons - Show in both view and edit modes */}
              {(isEditMode || isViewMode) && newContact.contactId && (
                <div className="mx-2 my-4 mb-4">
                  <div className="flex justify-around gap-4">
                    <button 
                      type="button"
                      onClick={handleMessageClick}
                      className="flex-1 bg-blue-100 rounded-lg p-3 flex flex-col items-center hover:bg-gray-100 transition-colors duration-200"
                    >
                      <div className="w-12 rounded-lg flex items-center justify-center mb-2">
                        <FontAwesomeIcon 
                          icon={faComment} 
                          className="text-blue-600"
                          size="lg"
                        />
                      </div>
                      <span className="text-sm text-gray-600">Message</span>
                    </button>

                    <button 
                      type="button"
                      onClick={handleCallClick}
                      className="flex-1 bg-green-100 rounded-lg p-3 flex flex-col items-center hover:bg-gray-100 transition-colors duration-200"
                    >
                      <div className="w-12 rounded-lg flex items-center justify-center mb-2">
                        <FontAwesomeIcon 
                          icon={faPhone} 
                          className="text-green-600"
                          size="lg"
                        />
                      </div>
                      <span className="text-sm text-gray-600">Call</span>
                    </button>

                    <button 
                      type="button"
                      onClick={handleDeleteContact}
                      className="flex-1 bg-red-100 rounded-lg p-3 flex flex-col items-center hover:bg-gray-100 transition-colors duration-200"
                    >
                      <div className="w-12 rounded-lg flex items-center justify-center mb-2">
                        <FontAwesomeIcon 
                          icon={faTrash} 
                          className="text-red-600"
                          size="lg"
                        />
                      </div>
                      <span className="text-sm text-gray-600">Delete</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Details/Form Container */}
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                {isViewMode ? (
                  // Details View
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">First Name</h3>
                      <h2 className="mt-1 text-lg font-semibold text-gray-900">{newContact.firstName}</h2>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Last Name</h3>
                      <h2 className="mt-1 text-lg font-semibold text-gray-900">{newContact.lastName}</h2>
                    </div>
                    {newContact.workPhone && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Work Phone</h3>
                        <h2 className="mt-1 text-lg font-semibold text-gray-900">{newContact.workPhone}</h2>
                      </div>
                    )}
                    {newContact.cellPhone && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Cell Phone</h3>
                        <h2 className="mt-1 text-lg font-semibold text-gray-900">{newContact.cellPhone}</h2>
                      </div>
                    )}
                    {newContact.homePhone && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Home Phone</h3>
                        <h2 className="mt-1 text-lg font-semibold text-gray-900">{newContact.homePhone}</h2>
                      </div>
                    )}
                    {newContact.email && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Email</h3>
                        <h2 className="mt-1 text-lg font-semibold text-gray-900">{newContact.email}</h2>
                      </div>
                    )}
                    {newContact.fax && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Fax</h3>
                        <h2 className="mt-1 text-lg font-semibold text-gray-900">{newContact.fax}</h2>
                      </div>
                    )}
                  </div>
                ) : (
                  // Edit Form
                  <form onSubmit={handleAddContact} className="space-y-4">
                    {/* Form Fields */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={newContact.firstName}
                        onChange={(e) => setNewContact({...newContact, firstName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={newContact.lastName}
                        onChange={(e) => setNewContact({...newContact, lastName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Work Phone
                      </label>
                      <input
                        type="tel"
                        value={newContact.workPhone}
                        onChange={(e) => setNewContact({...newContact, workPhone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cell Phone
                      </label>
                      <input
                        type="tel"
                        value={newContact.cellPhone}
                        onChange={(e) => setNewContact({...newContact, cellPhone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Home Phone
                      </label>
                      <input
                        type="tel"
                        value={newContact.homePhone}
                        onChange={(e) => setNewContact({...newContact, homePhone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fax
                      </label>
                      <input
                        type="tel"
                        value={newContact.fax}
                        onChange={(e) => setNewContact({...newContact, fax: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </form>
                )}
              </div>

              {/* Save Button - Show in both edit and new contact modes */}
              {!isViewMode && (
                <div className="relative bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
                  <button
                    onClick={handleAddContact}
                    style={(() => {
                      const styles = localStorage.getItem("messaging_styles");
                      let buttonStyles = {};
                      
                      if (styles) {
                        try {
                          const parsedStyles = JSON.parse(styles);
                          buttonStyles = {
                            backgroundColor: parsedStyles.message_buttons_color || '#3B82F6',
                            color: parsedStyles.message_buttons_font_color || '#FFFFFF'
                          };
                        } catch (e) {
                          console.error('Error parsing messaging styles:', e);
                        }
                      }
                      return buttonStyles;
                    })()}
                    className={`w-full py-3 rounded-lg transition-colors duration-200 font-medium
                      ${!localStorage.getItem("messaging_styles") ? 'bg-blue-500 text-white hover:bg-blue-600' : 
                      'hover:opacity-80'}`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>{isEditMode ? 'Save Changes' : 'Add Contact'}</span>
                      {sending && (
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                    </div>
                  </button>
                </div>
              )}
            </>
          ) : (
            // Contacts List View
            <>
              {/* Search Bar */}
              <div className="p-4">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search contacts..."
                    className="w-full pr-4 py-2 pl-10 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FontAwesomeIcon 
                    icon={faSearch}
                    className="absolute left-4 top-3 text-gray-400"
                  />
                </div>
              </div>

              {/* Contacts List */}
              <div className="flex-1 overflow-y-auto pr-7">
                {Object.entries(groupedContacts)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([letter, contacts]) => (
                    <div key={letter} ref={el => sectionRefs.current[letter] = el}>
                      <div className="sticky top-0 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-500">
                        {letter}
                      </div>
                      {contacts.map((contact, index) => (
                        <div
                          key={index}
                          onClick={() => handleContactClick(contact)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <div className="flex items-center">
                            <div 
                              style={(() => {
                                const styles = localStorage.getItem("messaging_styles");
                                let avatarStyles = {
                                  backgroundColor: '#EFF6FF', // default light blue bg
                                  color: '#3B82F6'  // default blue text
                                };
                                
                                if (styles) {
                                  try {
                                    const parsedStyles = JSON.parse(styles);
                                    // Make background color lighter by adding 20% opacity
                                    const bgColor = parsedStyles.message_buttons_color || '#3B82F6';
                                    avatarStyles = {
                                      backgroundColor: `${bgColor}`, // 20% opacity
                                      color: parsedStyles.message_buttons_font_color || '#FFFFFF'
                                    };
                                  } catch (e) {
                                    console.error('Error parsing messaging styles:', e);
                                  }
                                }
                                return avatarStyles;
                              })()}
                              className="w-10 h-10 rounded-full flex items-center justify-center font-semibold"
                            >
                              {contact.firstName && contact.firstName[0] ? contact.firstName[0].toUpperCase() : contact.lastName && contact.lastName[0] ? contact.lastName[0].toUpperCase() : ''}
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">
                                {contact.firstName} {contact.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {contact.workPhone || contact.cellPhone || contact.homePhone}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
              </div>

              {/* Alphabetical Index Bar */}
              <div 
                className="absolute right-0 top-1/2 transform -translate-y-1/2 flex flex-col justify-center h-full py-4 text-xs font-medium"
                onTouchStart={(e) => e.preventDefault()}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {availableLetters.map((letter) => (
                  <div
                    key={letter}
                    onTouchStart={(e) => handleTouchStart(e, letter)}
                    onClick={() => scrollToLetter(letter)}
                    className={`px-1 py-0.5 cursor-pointer ${
                      activeLetter === letter ? 'text-blue-500 font-bold scale-150' : 'text-gray-500'
                    }`}
                  >
                    {letter}
                  </div>
                ))}
              </div>

              {/* Letter Indicator */}
              {showLetterIndicator && activeLetter && (
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gray-800 bg-opacity-80 rounded-xl flex items-center justify-center text-4xl text-white font-bold">
                  {activeLetter}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Number Selection Popup */}
      {showNumberSelect && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg w-[90%] max-w-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <button 
                onClick={() => setShowNumberSelect(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <h3 className="text-lg font-semibold text-gray-800">
                {actionType === 'call' ? 'Choose number to make call' : 'Choose number to send message'}
              </h3>
              <div className="w-4"></div>
            </div>
            
            <div className="p-4">
              <select
                value={selectedNumber}
                onChange={(e) => setSelectedNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              >
                {getAvailableNumbers().map((item, index) => (
                  <option key={index} value={item.number}>
                    {item.label}: {item.number}
                  </option>
                ))}
              </select>

              <button
                onClick={handleNumberSelectAction}
                className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-colors duration-200 font-medium"
              >
                {actionType === 'call' ? 'Make Call' : 'Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactBook; 